import React, { useEffect, useMemo, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  Shield, 
  CheckCircle, 
  AlertTriangle, 
  FileText, 
  Download, 
  Eye,
  Brain,
  Target,
  TrendingUp,
  Clock,
  Users,
  Zap
} from 'lucide-react';
import { apiClient, RegulatoryChange } from '../lib/api';

type QualityBand = 'excellent' | 'good' | 'caution' | 'review';

export function AIQualityControl() {
  const [changes, setChanges] = useState<RegulatoryChange[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isActive = true;
    const load = async () => {
      try {
        setLoading(true);
        const data = await apiClient.getRecentChanges(200);
        if (!isActive) return;
        setChanges(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!isActive) return;
        setError('Unable to load live quality data right now.');
        console.error('AIQualityControl: failed to load changes', err);
      } finally {
        if (isActive) setLoading(false);
      }
    };
    load();
    return () => {
      isActive = false;
    };
  }, []);

  const metrics = useMemo(() => {
    const now = new Date();
    const total = changes.length;
    const recent = changes.filter(change => {
      const date = new Date(change.analysis_timestamp || change.discovered_date);
      return (now.getTime() - date.getTime()) / (1000 * 60 * 60) <= 48;
    });

    const withQuality = changes.filter(c => typeof c.quality_control?.quality_score === 'number');
    const avgQuality = withQuality.reduce((sum, c) => sum + (c.quality_control?.quality_score || 0), 0);
    const qualityScore = withQuality.length ? Math.round((avgQuality / withQuality.length) * 10) / 10 : null;

    const safeToUse = changes.filter(c => c.quality_control?.safe_to_use);
    const needsReview = changes.filter(c => c.quality_control && c.quality_control.safe_to_use === false);

    const upcomingDeadlines = changes
      .map(c => c.simplified_analysis?.compliance_deadline)
      .filter(Boolean)
      .map(dateStr => new Date(dateStr as string))
      .filter(d => !Number.isNaN(d.getTime()))
      .map(d => Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)))
      .filter(days => days >= 0)
      .sort((a, b) => a - b);

    const urgentDeadlines = upcomingDeadlines.filter(days => days <= 7);

    const highPrioritySteps = changes.reduce((count, change) => {
      const steps = change.simplified_analysis?.next_steps || [];
      return count + steps.filter(step => step.priority?.toLowerCase() === 'high').length;
    }, 0);

    const departments = Array.from(
      new Set(
        changes.flatMap(c => c.affected_departments || c.simplified_analysis?.who_this_affects?.departments || [])
      )
    );

    const latestTimestamp = changes
      .map(c => c.analysis_timestamp || c.discovered_date)
      .filter(Boolean)
      .map(ts => new Date(ts as string).getTime())
      .sort((a, b) => b - a)[0];

    const band: QualityBand =
      qualityScore && qualityScore >= 9
        ? 'excellent'
        : qualityScore && qualityScore >= 8
        ? 'good'
        : qualityScore && qualityScore >= 7
        ? 'caution'
        : 'review';

    return {
      total,
      recentCount: recent.length,
      qualityScore,
      band,
      safeToUseCount: safeToUse.length,
      needsReviewCount: needsReview.length,
      upcomingDeadlines,
      urgentDeadlineCount: urgentDeadlines.length,
      earliestDeadlineDays: upcomingDeadlines[0] ?? null,
      highPrioritySteps,
      departments,
      latestTimestamp,
    };
  }, [changes]);

  const renderQualityBadge = () => {
    switch (metrics.band) {
      case 'excellent':
        return <Badge className="bg-green-100 text-green-800">Excellent</Badge>;
      case 'good':
        return <Badge className="bg-blue-100 text-blue-800">Good</Badge>;
      case 'caution':
        return <Badge className="bg-yellow-100 text-yellow-800">Caution</Badge>;
      default:
        return <Badge className="bg-red-100 text-red-800">Review</Badge>;
    }
  };

  const formatTimestamp = (timestamp: number | undefined) => {
    if (!timestamp) return 'Unknown';
    const diffMinutes = Math.floor((Date.now() - timestamp) / (1000 * 60));
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  const loadingState = (
    <div className="min-h-[320px] flex items-center justify-center text-muted-foreground">
      Loading live quality metrics...
    </div>
  );

  const errorState = (
    <div className="min-h-[320px] flex items-center justify-center text-red-600">
      {error}
    </div>
  );

  return (
    <div className="flex-1 p-6 space-y-6 overflow-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold mb-1">AI Quality Control</h1>
          <p className="text-muted-foreground">
            Live view of analysis quality, deadlines, and required reviews
          </p>
        </div>
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <span>Data freshness:</span>
          <Badge variant="outline">{formatTimestamp(metrics.latestTimestamp || undefined)}</Badge>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Documents analyzed</span>
            <FileText className="w-5 h-5 text-gray-400" />
          </div>
          <div className="text-3xl font-bold">{metrics.total}</div>
          <div className="text-xs text-green-700 font-medium">
            {metrics.recentCount} new in last 48h
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Quality score</span>
            <TrendingUp className="w-5 h-5 text-blue-400" />
          </div>
          <div className="flex items-center space-x-2">
            <div className="text-3xl font-bold">
              {metrics.qualityScore !== null ? metrics.qualityScore : '—'}
              {metrics.qualityScore !== null && <span className="text-xl text-muted-foreground">/10</span>}
            </div>
            {renderQualityBadge()}
          </div>
          <div className="text-xs text-muted-foreground">
            {metrics.safeToUseCount} safe • {metrics.needsReviewCount} flagged
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Urgent deadlines</span>
            <AlertTriangle className="w-5 h-5 text-red-400" />
          </div>
          <div className="text-3xl font-bold">{metrics.urgentDeadlineCount}</div>
          <div className="text-xs text-muted-foreground">
            Earliest in {metrics.earliestDeadlineDays ?? '—'} days
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">High-priority steps</span>
            <Zap className="w-5 h-5 text-amber-400" />
          </div>
          <div className="text-3xl font-bold">{metrics.highPrioritySteps}</div>
          <div className="text-xs text-muted-foreground">Across current analyses</div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Dept coverage</span>
            <Users className="w-5 h-5 text-purple-400" />
          </div>
          <div className="text-3xl font-bold">{metrics.departments.length}</div>
          <div className="text-xs text-muted-foreground">Active departments</div>
        </Card>
      </div>

      {/* Quality Control Section */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="bg-primary p-2 rounded-lg">
            <Shield className="w-6 h-6 text-primary-foreground" />
          </div>
          <div>
            <h2 className="text-xl font-semibold">Analysis Quality Control</h2>
            <p className="text-sm text-muted-foreground">
              Verified against live regulatory documents and deadlines
            </p>
          </div>
        </div>
        <div className="flex space-x-3">
          <Button variant="outline" size="sm" className="flex items-center space-x-2">
            <Eye className="w-4 h-4" />
            <span>View detailed QC</span>
          </Button>
          <Button size="sm" className="bg-primary hover:bg-primary/90 flex items-center space-x-2">
            <Download className="w-4 h-4" />
            <span>Download summary</span>
          </Button>
        </div>
      </div>

      <Card className="p-6">
        {loading && loadingState}
        {!loading && error && errorState}
        {!loading && !error && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-green-800">Safe to use</span>
                  <CheckCircle className="w-4 h-4 text-green-600" />
                </div>
                <p className="text-lg font-semibold text-green-800">{metrics.safeToUseCount}</p>
                <p className="text-xs text-muted-foreground mt-1">Validated for operational use</p>
              </div>

              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-yellow-800">Expert review</span>
                  <AlertTriangle className="w-4 h-4 text-yellow-600" />
                </div>
                <p className="text-lg font-semibold text-yellow-800">{metrics.needsReviewCount}</p>
                <p className="text-xs text-muted-foreground mt-1">Flagged for human verification</p>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-800">Avg quality score</span>
                  <TrendingUp className="w-4 h-4 text-blue-600" />
                </div>
                <p className="text-lg font-semibold text-blue-800">
                  {metrics.qualityScore !== null ? metrics.qualityScore : 'No scores yet'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Based on backend QC grades</p>
              </div>

              <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-indigo-800">Upcoming deadlines</span>
                  <Clock className="w-4 h-4 text-indigo-600" />
                </div>
                <p className="text-lg font-semibold text-indigo-800">
                  {metrics.upcomingDeadlines.length}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Soonest in {metrics.earliestDeadlineDays ?? '—'} days
                </p>
              </div>
            </div>

            <div className="p-6 bg-blue-50 border border-blue-200 rounded-lg mb-6">
              <div className="flex items-center mb-4">
                <Target className="w-5 h-5 text-blue-600 mr-2" />
                <h4 className="text-base font-semibold text-blue-800">Quality signals</h4>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-blue-800">
                <div className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 text-blue-600" />
                  <span>{metrics.highPrioritySteps} high-priority actions detected</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 text-blue-600" />
                  <span>{metrics.departments.length} departments impacted</span>
                </div>
                <div className="flex items-start space-x-2">
                  <CheckCircle className="w-4 h-4 mt-0.5 text-blue-600" />
                  <span>{metrics.recentCount} fresh analyses in the last 48h</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="p-4 border border-border rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold">Confidence distribution</span>
                  {renderQualityBadge()}
                </div>
                <Progress
                  value={
                    metrics.qualityScore !== null
                      ? Math.min(100, Math.max(0, (metrics.qualityScore / 10) * 100))
                      : 0
                  }
                  className="h-2"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Aggregated from backend quality_control scores
                </p>
              </div>

              <div className="p-4 border border-border rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold">Deadlines requiring attention</span>
                  <Badge variant="outline">{metrics.urgentDeadlineCount} urgent</Badge>
                </div>
                <div className="space-y-2 text-sm text-muted-foreground">
                  {metrics.upcomingDeadlines.slice(0, 3).map((days, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <span>Compliance item #{idx + 1}</span>
                      <span className={days <= 7 ? 'text-red-600 font-medium' : ''}>
                        {days} day{days !== 1 ? 's' : ''}
                      </span>
                    </div>
                  ))}
                  {metrics.upcomingDeadlines.length === 0 && (
                    <div className="text-sm">No upcoming deadlines captured.</div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
